import { Box, Stack, Theme } from '@mui/material';
import { FC, useState } from 'react';
import Icon from '../../../icon';
import InlineToast from '../../../toasts/inline';
import { createUseStyles } from 'react-jss';
import { FileWithPath, useDropzone } from 'react-dropzone';
import FileIconImage from '@/assets/file-icon.svg';
import Text from '@/components/text';
import {
  countLinesInFile,
  getNiceNumber,
  LoadState,
  getNiceFilesize,
  pluralize,
} from '@/lib/helpers';
import FlatButton from '@/components/flat-button';
import { uploadPhraseFile } from '@/lib/services/classification.service';
import Loader from '@/components/loader';
import { IntentClassifierModel } from '@/lib/models/classifier';

interface Props {
  classifier: IntentClassifierModel;
  onUpload: () => void;
}

type FileWithLineCount = FileWithPath & { lineCount?: number };

const useStyles = createUseStyles((theme: Theme) => ({
  phraseUploader: {
    '& .dropzone': {
      boxSizing: 'border-box',
      cursor: 'pointer',
      padding: 16,
      backgroundColor: theme.palette.subtleGray,
      borderRadius: theme.shape.borderRadius,
      border: `1px dashed ${theme.palette.gray}`,
      color: theme.palette.darkGray,
    },
  },
  uploadPreview: {
    boxSizing: 'border-box',
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.gray}`,
  },
  fileIcon: {
    width: 40,
  },
  uploadProgressBar: {
    width: '100% !important', // @todo why is this being set in the tabs component?
  },
}));

const MAX_FILE_SIZE_KB = 1024 * 2;
const MAX_FILE_SIZE_MB = MAX_FILE_SIZE_KB / 1024;

const PhraseUploader: FC<Props> = ({ classifier, onUpload }) => {
  const styles = useStyles();

  const [uploadFile, setUploadFile] = useState<FileWithLineCount | null>(null);
  const [uploadError, setUploadError] = useState('');
  const [loadState, setLoadState] = useState<LoadState>('unloaded');

  const { getRootProps, getInputProps } = useDropzone({
    accept: { 'text/plain': ['.txt'], 'text/csv': ['.csv'] },
    onDrop: (acceptedFiles: FileWithPath[]) => {
      setUploadError('');
      if (!acceptedFiles.length) {
        return;
      }

      const file: FileWithLineCount = acceptedFiles[0];

      if (file.size > MAX_FILE_SIZE_KB * 1000) {
        setUploadFile(file);
        setUploadError(`Please limit the file to a maximum size of ${MAX_FILE_SIZE_MB}mb`);
        return;
      }

      countLinesInFile(file)
        .then((lineCount) => {
          file.lineCount = lineCount;
          setUploadError('');
          setUploadFile(file);
        })
        .catch((err) => {
          console.error(err);
          setUploadError('Unable to parse the file. Please check the file format.');
        });
    },
  });

  const handleRemove = () => {
    setUploadFile(null);
    setUploadError('');
  };

  const handleUpload = () => {
    if (!uploadFile) {
      return;
    }

    setLoadState('loading');

    uploadPhraseFile(classifier.id, uploadFile).then(({ updated, error, fieldErrors }) => {
      setLoadState('loaded');

      if (!updated || error || fieldErrors) {
        setUploadError(
          error ||
            fieldErrors?.map(({ msg }) => msg).join(', ') ||
            'The file could not be uploaded. Please try again.'
        );
        return;
      }

      onUpload();
      setUploadFile(null);
      setUploadError('');
    });
  };

  const hasError = !!uploadError;
  const hasFile = !!uploadFile;
  const isLoading = loadState === 'loading';
  const lineCount = uploadFile?.lineCount || 0;

  return (
    <Box className={styles.phraseUploader}>
      {!hasFile && (
        <Stack
          {...getRootProps({ className: 'dropzone' })}
          flexDirection="row"
          alignItems="center"
          justifyContent="center"
        >
          <input {...getInputProps()} />
          <Box mr={1}>
            <Icon name="upload" size="large" />
          </Box>
          <Box>Drag and drop a text or CSV file, or click to choose a file.</Box>
        </Stack>
      )}

      {hasFile && (
        <Box p={2} className={styles.uploadPreview}>
          <Stack direction="row" gap={1} alignItems="flex-start">
            <img src={FileIconImage} className={styles.fileIcon} />
            <Box flexGrow={1}>
              <Text color="black">{uploadFile.name}</Text>
              <Text size="small" color="grey">
                {getNiceFilesize(uploadFile.size)} &middot; {getNiceNumber(lineCount)}{' '}
                {pluralize(lineCount, 'message')}
              </Text>
            </Box>
          </Stack>

          {hasError && (
            <Box mt={2}>
              <InlineToast message={uploadError} level="error" />
            </Box>
          )}

          <Box width="100%" mt={2}>
            {isLoading && <Loader className={styles.uploadProgressBar} />}

            {!isLoading && (
              <Stack direction="row" gap={2} justifyContent="flex-end" flexGrow={1}>
                {!hasError && (
                  <FlatButton
                    icon="upload"
                    label="Start Upload"
                    onClick={handleUpload}
                    color="link"
                    size="small"
                  />
                )}
                <FlatButton
                  icon="delete"
                  label="Remove File"
                  onClick={handleRemove}
                  color="error"
                  size="small"
                />
              </Stack>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default PhraseUploader;
